/**
 * Product Info Section - JavaScript
 * Handles variant selection, quantity, add to cart, and sliders
 */

(function() {
  'use strict';

  // Wait for DOM and Swiper
  function init() {
    document.querySelectorAll('.product-info').forEach(initSection);
  }

  function initSection(section) {
    const sectionId = section.dataset.sectionId;
    const productId = section.dataset.productId;
    const productJson = document.getElementById(`product-json-${sectionId}`);
    const isPreorder = section.dataset.preorder === 'true';

    if (!productJson) return;

    const product = JSON.parse(productJson.textContent);
    let currentVariant = product.variants.find(v => v.available) || product.variants[0];

    // Initialize mobile slider
    initMobileSlider(section, sectionId);

    // Variant picker
    initVariantPicker(section, product, (variant) => {
      currentVariant = variant;
      updatePrice(section, variant);
      updateBuyButton(section, variant);
    });

    // Quantity controls
    initQuantity(section);

    // Add to cart (with pre-order property if applicable)
    initAddToCart(section, () => currentVariant, isPreorder);

    // Pre-order features
    if (isPreorder) {
      initPreorderCountdown(section);
      initPreorderCounter(section);
    }
  }

  // Mobile Slider
  function initMobileSlider(section, sectionId) {
    const sliderEl = section.querySelector(`#product-mobile-slider-${sectionId}`);
    if (!sliderEl || typeof Swiper === 'undefined') {
      // Retry if Swiper not loaded yet
      if (sliderEl) {
        setTimeout(() => initMobileSlider(section, sectionId), 100);
      }
      return;
    }

    new Swiper(sliderEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      pagination: {
        el: sliderEl.querySelector('.product-info__slider-pagination'),
        clickable: true,
      },
      navigation: {
        prevEl: sliderEl.querySelector('.product-info__slider-prev'),
        nextEl: sliderEl.querySelector('.product-info__slider-next'),
      },
    });
  }

  // Variant Picker
  function initVariantPicker(section, product, onChange) {
    const inputs = section.querySelectorAll('.product-info__option-btn input');

    inputs.forEach(input => {
      input.addEventListener('change', () => {
        // Update active states
        const optionGroup = input.closest('.product-info__option-buttons');
        optionGroup.querySelectorAll('.product-info__option-btn').forEach(btn => {
          btn.classList.remove('is-active');
        });
        input.closest('.product-info__option-btn').classList.add('is-active');

        // Find matching variant
        const selectedOptions = [];
        section.querySelectorAll('.product-info__option-btn.is-active input').forEach(activeInput => {
          selectedOptions.push(activeInput.value);
        });

        const variant = product.variants.find(v => {
          return v.options.every((opt, i) => opt === selectedOptions[i]);
        });

        if (variant) {
          // Update URL
          const url = new URL(window.location);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);

          onChange(variant);
        }
      });
    });
  }

  // Update price display
  function updatePrice(section, variant) {
    const priceEl = section.querySelector('[data-product-price]');
    if (!priceEl) return;

    const currentEl = priceEl.querySelector('.product-info__price-current');
    const compareEl = priceEl.querySelector('.product-info__price-compare');

    if (currentEl) {
      currentEl.textContent = formatMoney(variant.price);
      currentEl.classList.toggle('is-sale', variant.compare_at_price > variant.price);
    }

    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  // Update buy button
  function updateBuyButton(section, variant) {
    const btn = section.querySelector('[data-add-to-cart]');
    const textEl = section.querySelector('[data-add-text]');

    if (!btn) return;

    btn.dataset.variantId = variant.id;
    btn.disabled = !variant.available;
    btn.classList.toggle('is-sold-out', !variant.available);

    if (textEl) {
      const addText = btn.dataset.addText || 'ADD TO BAG';
      const soldText = btn.dataset.soldText || 'SOLD OUT';
      textEl.textContent = variant.available ? addText : soldText;
    }

    // Toggle Attentive waitlist by adding/removing trigger from DOM
    const eligibilityJson = section.querySelector('#attn-waitlist-eligibility');
    if (eligibilityJson) {
      try {
        const eligibility = JSON.parse(eligibilityJson.textContent);
        const isEligible = eligibility[String(variant.id)] === true;
        const existing = section.querySelector('.attn-waitlist-trigger');
        if (isEligible && !existing) {
          const trigger = document.createElement('div');
          trigger.id = 'attn-waitlist-trigger';
          trigger.className = 'attn-waitlist-trigger';
          trigger.style.width = '100%';
          eligibilityJson.after(trigger);
        } else if (!isEligible && existing) {
          existing.remove();
        }
      } catch (e) {
        // Silent fail
      }
    }
  }

  // Quantity controls
  function initQuantity(section) {
    const minusBtn = section.querySelector('[data-qty-minus]');
    const plusBtn = section.querySelector('[data-qty-plus]');
    const input = section.querySelector('[data-qty-input]');

    if (!input) return;

    minusBtn?.addEventListener('click', () => {
      const val = parseInt(input.value, 10) || 1;
      input.value = Math.max(1, val - 1);
    });

    plusBtn?.addEventListener('click', () => {
      const val = parseInt(input.value, 10) || 1;
      input.value = Math.min(999, val + 1);
    });

    input.addEventListener('change', () => {
      const val = parseInt(input.value, 10);
      if (isNaN(val) || val < 1) input.value = 1;
      if (val > 999) input.value = 999;
    });
  }

  // Add to cart
  function initAddToCart(section, getVariant, isPreorder) {
    const btn = section.querySelector('[data-add-to-cart]');
    if (!btn) return;

    btn.addEventListener('click', async (e) => {
      e.preventDefault();

      const variant = getVariant();
      if (!variant || !variant.available) return;

      // Check pre-order limit
      if (isPreorder) {
        const limit = parseInt(section.dataset.preorderLimit, 10);
        const claimedEl = section.querySelector('[data-preorder-claimed]');
        if (limit && claimedEl) {
          const claimed = parseInt(claimedEl.textContent, 10) || 0;
          if (claimed >= limit) {
            return;
          }
        }
      }

      const quantityInput = section.querySelector('[data-qty-input]');
      const quantity = parseInt(quantityInput?.value, 10) || 1;

      btn.disabled = true;
      const originalText = btn.querySelector('[data-add-text]')?.textContent;

      // Build cart body with pre-order properties if applicable
      const cartBody = {
        id: variant.id,
        quantity: quantity,
      };

      if (isPreorder) {
        cartBody.properties = {
          '_pre-order': 'true',
          '_preorder-date': new Date().toISOString().split('T')[0]
        };
        const shipDateEl = section.querySelector('.product-info__preorder-ship-date span')
          || section.querySelector('.product-info__preorder-label-ship');
        if (shipDateEl) {
          cartBody.properties['Estimated Ship Date'] = shipDateEl.textContent.replace('EST. SHIP :: ', '').trim();
        }
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cartBody),
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        // Success - update cart count if function exists
        if (typeof window.updateCartCount === 'function') {
          window.updateCartCount();
        }

        // Show added feedback
        const textEl = btn.querySelector('[data-add-text]');
        if (textEl) {
          textEl.textContent = 'ADDED!';
          setTimeout(() => {
            textEl.textContent = originalText;
          }, 1500);
        }

        // Trigger cart drawer if it exists
        document.dispatchEvent(new CustomEvent('cart:added', { detail: { variant, quantity } }));

        // Open the cart drawer
        if (typeof window.openCartDrawer === 'function') {
          window.openCartDrawer();
        }

      } catch (err) {
        console.error('Add to cart error:', err);
        const textEl = btn.querySelector('[data-add-text]');
        if (textEl) {
          textEl.textContent = 'ERROR';
          setTimeout(() => {
            textEl.textContent = originalText;
          }, 1500);
        }
      } finally {
        btn.disabled = !variant.available;
      }
    });
  }

  // Format money (basic - adjust for your store's currency)
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Pre-order Countdown Timer
  function initPreorderCountdown(section) {
    const countdownEl = section.querySelector('[data-preorder-countdown]');
    const closeDate = section.dataset.preorderClose;
    if (!countdownEl || !closeDate) return;

    const target = new Date(closeDate).getTime();
    const daysEl = countdownEl.querySelector('[data-countdown-days]');
    const hoursEl = countdownEl.querySelector('[data-countdown-hours]');
    const minsEl = countdownEl.querySelector('[data-countdown-mins]');
    const secsEl = countdownEl.querySelector('[data-countdown-secs]');
    const btn = section.querySelector('[data-add-to-cart]');
    const textEl = section.querySelector('[data-add-text]');

    function update() {
      const now = Date.now();
      const diff = target - now;

      if (diff <= 0) {
        // Pre-order window closed
        daysEl.textContent = '00';
        hoursEl.textContent = '00';
        minsEl.textContent = '00';
        secsEl.textContent = '00';
        countdownEl.classList.add('is-expired');
        if (btn) {
          btn.disabled = true;
          btn.classList.add('is-sold-out');
        }
        if (textEl) textEl.textContent = 'PRE-ORDER CLOSED';
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const secs = Math.floor((diff % (1000 * 60)) / 1000);

      daysEl.textContent = String(days).padStart(2, '0');
      hoursEl.textContent = String(hours).padStart(2, '0');
      minsEl.textContent = String(mins).padStart(2, '0');
      secsEl.textContent = String(secs).padStart(2, '0');

      requestAnimationFrame(() => setTimeout(update, 1000));
    }

    update();
  }

  // Pre-order Counter / Limit
  // Source of truth: claimed = preorder_limit - sum(max(0, variant.inventory_quantity)).
  // Server renders the initial value into [data-preorder-claimed-initial]; we re-fetch
  // /products/{handle}.js when the tab regains focus. Optimistic +1 on cart:added is
  // never reversed by a refresh (refresh only ratchets the bar upward).
  function initPreorderCounter(section) {
    const counterEl = section.querySelector('[data-preorder-counter]');
    const limit = parseInt(section.dataset.preorderLimit, 10);
    if (!counterEl || !limit) return;

    const claimedEl = counterEl.querySelector('[data-preorder-claimed]');
    const fillEl = counterEl.querySelector('[data-preorder-fill]');
    const btn = section.querySelector('[data-add-to-cart]');
    const textEl = section.querySelector('[data-add-text]');
    const productHandle = section.dataset.productHandle;

    function applyClaimed(claimed) {
      const safe = Math.max(0, Math.min(claimed, limit));
      claimedEl.textContent = safe;
      if (fillEl) {
        fillEl.style.width = Math.min((safe / limit) * 100, 100) + '%';
      }
      if (safe >= limit) {
        counterEl.classList.add('is-full');
        if (btn) {
          btn.disabled = true;
          btn.classList.add('is-sold-out');
        }
        if (textEl) textEl.textContent = 'PRE-ORDER FULL';
      } else {
        counterEl.classList.remove('is-full');
      }
    }

    // claimed = sum of abs(negative variant inventory). Operating model:
    // "Continue selling when out of stock" + variants start at 0; each paid
    // order pushes inventory negative, so abs(negative) = pre-orders claimed.
    function computeClaimedFromVariants(productData) {
      let claimed = 0;
      if (productData && Array.isArray(productData.variants)) {
        productData.variants.forEach(v => {
          const q = typeof v.inventory_quantity === 'number' ? v.inventory_quantity : 0;
          if (q < 0) claimed += Math.abs(q);
        });
      }
      return Math.min(claimed, limit);
    }

    // Initial render — prefer server-rendered value, fall back to product JSON.
    const initialFromServer = parseInt(section.dataset.preorderClaimedInitial, 10);
    if (!isNaN(initialFromServer)) {
      applyClaimed(initialFromServer);
    } else {
      const productJson = document.getElementById(`product-json-${section.dataset.sectionId}`);
      if (productJson) {
        try {
          const product = JSON.parse(productJson.textContent);
          applyClaimed(computeClaimedFromVariants(product));
        } catch (e) {
          applyClaimed(0);
        }
      } else {
        applyClaimed(0);
      }
    }

    // Optimistic +1 for the current visitor's add-to-cart action.
    document.addEventListener('cart:added', () => {
      const current = parseInt(claimedEl.textContent, 10) || 0;
      applyClaimed(current + 1);
    });

    // Tab-focus / bfcache refresh — only ratchet upward so optimistic adds aren't undone.
    async function refreshFromServer() {
      if (!productHandle) return;
      try {
        const res = await fetch(`/products/${productHandle}.js`, {
          cache: 'no-store',
          headers: { 'Accept': 'application/json' }
        });
        if (!res.ok) return;
        const data = await res.json();
        const computed = computeClaimedFromVariants(data);
        const current = parseInt(claimedEl.textContent, 10) || 0;
        if (computed > current) applyClaimed(computed);
      } catch (_) {
        // silent
      }
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refreshFromServer();
    });
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) refreshFromServer();
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Reinitialize on section reload (Shopify editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('.product-info')) {
      init();
    }
  });

})();
